import React, { useState, useEffect } from 'react';
import './PropertyCalendar.css';

const PropertyCalendar = ({ propertyId, apiEndpoint }) => {
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchCalendar();
  }, [propertyId, startDate]);

  const fetchCalendar = async () => {
    try {
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 90); // 3 months

      const response = await fetch(
        `${apiEndpoint}/properties/${propertyId}/occupancy?start_date=${startDate}&end_date=${endDate.toISOString().split('T')[0]}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      setCalendar(data.calendar || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return '#28a745';
      case 'booked': return '#007bff';
      case 'blocked': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponível';
      case 'booked': return 'Reservado';
      case 'blocked': return 'Bloqueado';
      default: return 'Desconhecido';
    }
  };

  const groupByMonth = () => {
    const months = {};
    calendar.forEach(day => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!months[monthKey]) {
        months[monthKey] = [];
      }
      months[monthKey].push(day);
    });
    return months;
  };

  if (loading) {
    return <div className="calendar-loading">Carregando calendário...</div>;
  }

  const monthsData = groupByMonth();

  return (
    <div className="property-calendar">
      <div className="calendar-header">
        <h3>Calendário de Ocupação</h3>
        <div className="calendar-controls">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="form-input"
          />
          <button onClick={fetchCalendar} className="btn btn-primary btn-sm">
            Atualizar
          </button>
        </div>
      </div>

      {summary && (
        <div className="calendar-summary">
          <div className="summary-item">
            <span className="summary-label">Total de dias:</span>
            <span className="summary-value">{summary.total_days}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Disponíveis:</span>
            <span className="summary-value" style={{ color: '#28a745' }}>{summary.available_days}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Reservados:</span>
            <span className="summary-value" style={{ color: '#007bff' }}>{summary.booked_days}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Bloqueados:</span>
            <span className="summary-value" style={{ color: '#dc3545' }}>{summary.blocked_days}</span>
          </div>
        </div>
      )}

      <div className="calendar-legend">
        <span className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#28a745' }}></span>
          Disponível
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#007bff' }}></span>
          Reservado
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#dc3545' }}></span>
          Bloqueado
        </span>
      </div>

      <div className="calendar-months">
        {Object.keys(monthsData).map(monthKey => {
          const [year, month] = monthKey.split('-');
          const monthName = new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
          const days = monthsData[monthKey];

          // Get first day of month to calculate offset
          const firstDay = new Date(days[0].date);
          const dayOfWeek = firstDay.getDay();

          return (
            <div key={monthKey} className="calendar-month">
              <h4 className="month-title">{monthName}</h4>
              <div className="calendar-grid">
                <div className="calendar-weekdays">
                  <div className="weekday">Dom</div>
                  <div className="weekday">Seg</div>
                  <div className="weekday">Ter</div>
                  <div className="weekday">Qua</div>
                  <div className="weekday">Qui</div>
                  <div className="weekday">Sex</div>
                  <div className="weekday">Sáb</div>
                </div>
                <div className="calendar-days">
                  {/* Empty cells for offset */}
                  {Array.from({ length: dayOfWeek }).map((_, i) => (
                    <div key={`empty-${i}`} className="calendar-day empty"></div>
                  ))}

                  {days.map(day => {
                    const date = new Date(day.date);
                    const dayNum = date.getDate();

                    return (
                      <div
                        key={day.date}
                        className={`calendar-day ${day.status}`}
                        style={{ borderLeftColor: getStatusColor(day.status) }}
                        title={`${dayNum} - ${getStatusLabel(day.status)}`}
                      >
                        <div className="day-number">{dayNum}</div>
                        {day.booking && (
                          <div className="day-tooltip">
                            <strong>Reserva</strong><br />
                            {day.booking.guest_name}<br />
                            {new Date(day.booking.check_in).toLocaleDateString('pt-BR')} - {new Date(day.booking.check_out).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                        {day.date_block && (
                          <div className="day-tooltip">
                            <strong>Bloqueado</strong><br />
                            {day.date_block.reason}<br />
                            {day.date_block.notes && <span>{day.date_block.notes}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyCalendar;
